/**
 * Schémas pour les annonces externes ingérées via API tierces (V8 — mai 2026).
 *
 * Workflow :
 *   n8n (ou autre orchestrateur) → POST /external-offers/ingest
 *   → status=pending_validation
 *   → responsable valide via /responsable/external-offers/:id/validate
 *   → création d'une JobOffer interne.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * Body de POST /external-offers/ingest.
 * Le payload est normalisé Stelvyo — l'ingéreur (n8n) doit transformer la
 * réponse de l'API source vers ce format.
 */
export const externalOfferIngestSchema = z.object({
    source: z.enum([
        'france_travail', 'apec', 'adzuna', 'jooble', 'eures', 'hellowork',
        'lhotellerie_restauration', 'gestmax', 'elior', 'indeed', 'wizbii',
        'la_bonne_alternance',
        'accor', 'accor_corpo', 'barriere', 'sodexo', // V11 — SmartRecruiters
        'manual', 'autre',
    ]),
    externalId: z.string().trim().min(1).max(200),
    title: z.string().trim().min(3).max(300),
    description: z.string().trim().min(20).max(10000),
    contractType: z.string().trim().min(2).max(60),
    // Tous les champs optionnels acceptent null (n8n envoie null pour les
    // champs manquants), undefined, ou "" — tous normalisés à undefined.
    romeCode: z.string().trim().max(20).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    employerName: z.string().trim().min(2).max(200),
    employerLogoUrl: z.string().trim().url().max(500).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    location: z.string().trim().max(200).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    commune: z.string().trim().max(120).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    postalCode: z.string().trim().max(10).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    region: z.string().trim().max(120).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    applicationUrl: z.string().trim().url().max(500).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    applicationEmail: z.string().trim().email().max(200).nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    postedAt: z.coerce.date(),
    expiresAt: z.coerce.date().nullish(),
    // Formation cible suggérée par l'ingéreur (optionnel) — n8n peut faire
    // un mapping ROME → formationId si configuré.
    suggestedFormationId: uuidSchema.nullish().or(z.literal('')).transform((v) => (v ? v : undefined)),
    // Copie brute du payload source (debug / re-traitement).
    rawData: z.unknown().optional(),
});
/**
 * Body de POST /responsable/external-offers/:id/validate.
 * Le responsable peut sélectionner une ou plusieurs formations cibles
 * (une JobOffer indépendante est créée pour chaque formation), choisir
 * l'expiration et la visibilité de l'annonce convertie.
 *
 * V10.1 : formationId (single) → formationIds (array min 1) pour
 * permettre la publication simultanée vers plusieurs formations.
 */
export const externalOfferValidateSchema = z.object({
    formationIds: z.array(uuidSchema).min(1, 'Sélectionnez au moins une formation.'),
    expiresAt: z.coerce.date().optional().nullable(),
    visibility: z
        .enum(['public', 'restricted_formations', 'restricted_establishment'])
        .default('restricted_establishment'),
    allowedFormationIds: z.array(z.string().uuid()).optional().default([]),
    restrictedToEstablishmentId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
});
/**
 * Body de DELETE /responsable/external-offers/:id (rejet).
 */
export const externalOfferRejectSchema = z.object({
    reason: z.string().trim().max(500).optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
});
//# sourceMappingURL=externalJobOffer.js.map